"use client";

import { useState } from "react";
import { Pill } from "@/components/Icons";
import { useStock } from "@/context/StockContext";

export function VentesView() {
  const {
    factures,
    produits,
    findProduit,
    lignesAAssocier,
    associerLigne,
    ignorerLigne,
    showToast,
    syncing,
  } = useStock();
  const aa = lignesAAssocier();
  const [sels, setSels] = useState<Record<string, string>>({});
  const [retenir, setRetenir] = useState<Record<string, boolean>>({});

  const stat = {
    ok: <Pill kind="ok">Article</Pill>,
    associer: <Pill kind="warn">À associer</Pill>,
    ignore: <Pill kind="info">Port ou remise</Pill>,
  };
  const articles = factures.reduce(
    (n, f) => n + f.lignes.filter((l) => l.statut === "ok").length,
    0,
  );
  const ttc = factures.reduce((n, f) => n + (f.avoir ? -(f.montantTtc ?? 0) : (f.montantTtc ?? 0)), 0);

  return (
    <>
      <p className="muted" style={{ maxWidth: "68ch", marginBottom: 18 }}>
        Factures clients Pennylane depuis février, lues avec Synchroniser. Le canal vient de la
        commande : Hotte, Ventil ou Ventilationpro.
      </p>
      <div className="kpis" style={{ marginBottom: 18 }}>
        <div className="kpi">
          <span className="eyebrow">Factures</span>
          <b>{factures.length}</b>
          <span className="muted">depuis février</span>
        </div>
        <div className="kpi">
          <span className="eyebrow">Lignes d&apos;articles</span>
          <b>{articles}</b>
          <span className="muted">hors port et remises</span>
        </div>
        <div className="kpi">
          <span className="eyebrow">Total TTC</span>
          <b>
            {ttc.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </b>
          <span className="muted">avoirs déduits</span>
        </div>
      </div>
      {aa.length ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-h">
            <h2>Lignes à associer</h2>
            <Pill kind="warn">{aa.length}</Pill>
          </div>
          <div className="map">
            {aa.map((l) => (
              <div className="map-row" key={l.id}>
                <div className="src">
                  <span className="muted" style={{ fontSize: ".82rem" }}>
                    {l.num} · {l.canal}
                  </span>
                  <b>« {l.texte} »</b>
                  <span className="muted" style={{ fontSize: ".86rem" }}>
                    Quantité vendue : {l.qte}
                  </span>
                </div>
                <div className="field">
                  <label htmlFor={`as-${l.id}`}>Article du stock</label>
                  <select
                    id={`as-${l.id}`}
                    value={sels[l.id!] ?? ""}
                    onChange={(e) => setSels((s) => ({ ...s, [l.id!]: e.target.value }))}
                  >
                    <option value="">— Choisir l&apos;article —</option>
                    {produits.map((p) => (
                      <option key={p.ref} value={p.ref}>
                        {p.nom} · {p.ref}
                      </option>
                    ))}
                  </select>
                  <label className="remember">
                    <input
                      type="checkbox"
                      checked={retenir[l.id!] ?? true}
                      onChange={(e) => setRetenir((r) => ({ ...r, [l.id!]: e.target.checked }))}
                    />{" "}
                    Retenir pour les prochaines factures
                  </label>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="btn small"
                    type="button"
                    onClick={() => ignorerLigne(l.id!)}
                  >
                    Pas un article
                  </button>
                  <button
                    className="btn primary small"
                    type="button"
                    onClick={() => {
                      const ref = sels[l.id!];
                      if (!ref) return showToast("Choisis l'article correspondant");
                      associerLigne(l.id!, ref, retenir[l.id!] ?? true);
                    }}
                  >
                    Associer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <h2 style={{ marginBottom: 10 }}>Factures lues</h2>
      {!factures.length && (
        <p className="muted">{syncing ? "Lecture des factures Pennylane…" : "Aucune facture depuis février."}</p>
      )}
      <div className="inv">
        {factures.map((f, idx) => (
          <details key={f.id ?? `${f.num}-${f.canal}-${f.quand}-${idx}`}>
            <summary>
              <span>
                <b className="mono">{f.num}</b>{" "}
                <span className="muted" style={{ fontSize: ".86rem" }}>
                  · {f.quand}
                  {f.client ? ` · ${f.client}` : ""}
                </span>
              </span>
              <span className="tag">{f.avoir ? `Avoir · ${f.canal}` : f.canal}</span>
              <span className="mono">
                {f.avoir ? "−" : ""}
                {(f.montantTtc ?? 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
              </span>
              {f.lignes.some((l) => l.statut === "associer") ? (
                <Pill kind="warn">À associer</Pill>
              ) : (
                <Pill kind="ok">{f.avoir ? "Avoir" : "Vente"}</Pill>
              )}
            </summary>
            <ul className="lines list">
              {f.lignes.map((l, i) => (
                <li key={i}>
                  <span>
                    {l.ref ? findProduit(l.ref)?.nom : l.texte}
                    {l.ref ? (
                      <>
                        {" "}
                        <span className="mono muted">{l.ref}</span>
                      </>
                    ) : null}
                  </span>
                  <span className={`delta ${l.statut !== "ok" ? "zero" : f.avoir ? "plus" : "minus"}`}>
                    {l.statut === "ok" ? `${f.avoir ? "+" : "-"}${l.qte}` : `×${l.qte}`}
                  </span>
                  {stat[l.statut]}
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </>
  );
}
